import { z } from 'zod'
import { EVENT_STATUSES, EVENT_TYPES, PAYMENT_MODES, PAYMENT_MODE_QR } from '../constants.js'
import { zBaht, zDate, zEmail, uniqueBy } from '../zod-utils.js'

/**
 * One size, with everything about that size.
 *
 * The measurements used to live in a parallel `sizeChart` array keyed by the same
 * code, which meant adding a size was two edits in two places that nothing forced
 * to agree — and the organiser could only edit one of them from /admin, so the
 * table buyers read could not be corrected without a re-seed. Optional because an
 * event that sells a membership has sizes with nothing to measure.
 */
export const SizeOptionSchema = z.object({
  code: z.string().trim().min(1).max(10),
  /** Added to the item's basePrice. Shown on the size button as "+40". */
  extra: zBaht,
  /** Inches, as the garment trade quotes them. */
  chest: z.number().positive().optional(),
  length: z.number().positive().optional(),
})
export type SizeOption = z.infer<typeof SizeOptionSchema>

/**
 * A colourway of an item.
 *
 * A separate axis rather than a separate sku, because the factory order is counted
 * as colour × size and the buyer picks one shirt, not two products. `hex` is what
 * the picker swatch shows; `image` is the photo of that colour, which is the thing
 * people actually decide on.
 */
export const ColorOptionSchema = z.object({
  code: z.string().trim().regex(/^[A-Z0-9_-]{1,16}$/, 'รหัสสีต้องเป็น A-Z 0-9 _ - ไม่เกิน 16 ตัว'),
  name: z.string().trim().min(1, 'กรุณากรอกชื่อสี').max(40),
  hex: z.string().trim().regex(/^#[0-9a-fA-F]{6}$/, 'สีต้องเป็นรหัส HEX เช่น #1d4ed8'),
  /** Photo of this colourway. Uploaded by the organiser; blank falls back to the item image. */
  image: z.string().trim().optional(),
  /** Some colourways cost more to print. Usually 0. */
  extra: zBaht.default(0),
})
export type ColorOption = z.infer<typeof ColorOptionSchema>

export const EventItemSchema = z
  .object({
    sku: z.string().trim().regex(/^[A-Z0-9_-]{1,12}$/, 'sku ต้องเป็น A-Z 0-9 _ - ไม่เกิน 12 ตัว'),
    name: z.string().trim().min(1, 'กรุณากรอกชื่อแบบ'),
    basePrice: zBaht,
    image: z.string().trim().optional(),
    /**
     * Shown under the measurement table. Garment makers quote a tolerance, and a
     * buyer who never saw it reads a half-inch difference as the wrong size.
     */
    sizeNote: z.string().trim().max(200).optional(),
    /**
     * Empty means this item has no colour choice at all — a membership fee has no
     * colourway. The order form hides the row and the order line carries no colour.
     */
    colors: z.array(ColorOptionSchema).default([]),
    sizes: z.array(SizeOptionSchema).min(1, 'ต้องมี size อย่างน้อย 1 size'),
  })
  .refine((item) => uniqueBy(item.sizes, (size) => size.code), {
    message: 'size ซ้ำกัน',
    path: ['sizes'],
  })
  .refine((item) => uniqueBy(item.colors, (color) => color.code), {
    message: 'รหัสสีซ้ำกัน',
    path: ['colors'],
  })
export type EventItem = z.infer<typeof EventItemSchema>

export const ShippingSchema = z.object({
  enabled: z.boolean(),
  /** Per order, not per item. */
  fee: zBaht,
})

/**
 * Who the confirmation comes FROM.
 *
 * The e-mail bodies live in `functions/src/event/emails/` — in git, reviewed, and
 * unit-tested — rather than in a Brevo template. A template id is a number that
 * has to match something in another company's dashboard: it drifts, it is invisible
 * in code review, and a new event cannot send a single e-mail until somebody
 * remembers to create three of them by hand.
 *
 * `email` must be a VERIFIED sender in Brevo or the send is rejected.
 */
export const MailSenderSchema = z.object({
  name: z.string().trim().min(1),
  email: zEmail,
})
export type MailSender = z.infer<typeof MailSenderSchema>

export const EventConfigSchema = z
  .object({
    type: z.enum(EVENT_TYPES),
    title: z.string().trim().min(1, 'กรุณากรอกชื่องาน'),
    subtitle: z.string().trim().optional(),
    coverImage: z.string().trim().optional(),
    closeAt: zDate,
    status: z.enum(EVENT_STATUSES),
    orderPrefix: z.string().trim().regex(/^[A-Z]{2,5}$/, 'orderPrefix ต้องเป็นตัวพิมพ์ใหญ่ 2-5 ตัว'),
    /** A pending order older than this is swept to `expired`. */
    /**
     * How long an order stays payable, in MINUTES.
     *
     * Minutes, not hours, because the real bound is the payment provider's: Beam
     * caps a PromptPay QR at 30 minutes whatever we ask for. An order that outlives
     * its own QR is unpayable but still says 'awaiting payment' — so this wants to
     * land just past the QR, and 'just past' is not expressible in whole hours.
     */
    pendingExpiryMinutes: z.number().int().min(5).max(43200).default(45),
    paymentMode: z.enum(PAYMENT_MODES).default(PAYMENT_MODE_QR),
    /*
     * The tax already inside the item price, shown so a buyer can see where their
     * money goes. 0 hides the whole breakdown.
     *
     * This is the supplier's VAT passed through, NOT tax the organiser collects —
     * they are not registered, so nothing built on this may be worded as though an
     * invoice could be issued for it. Defaulted rather than required so configs
     * written before it existed still parse.
     */
    vatRate: z.number().min(0).max(1).default(0),
    /*
     * The ONE address a buyer is told to write to — on the order page and at the
     * foot of every e-mail.
     *
     * Single by design. It used to be `ownerEmails: string[]`, doubling as "who gets
     * copied on confirmations", and a second copy of the same fact lived in
     * `buildTime.contactUrl` for the web pages. Two lists and a baked-in string for
     * one question — "who do I contact?" — is three places to forget.
     *
     * Who may SIGN IN is a different question with a different answer, and lives in
     * the `adminAccess` collection.
     */
    contactEmail: zEmail,
    sender: MailSenderSchema,
    shipping: ShippingSchema,
    items: z.array(EventItemSchema).min(1, 'ต้องมีสินค้าอย่างน้อย 1 แบบ'),

    /**
     * Stamped by `adminUpdateEvent`. Their presence is how `import-event.ts` knows
     * the organiser has edited this config in the admin UI, so re-seeding would
     * silently throw their work away.
     */
    updatedAt: zDate.optional(),
    /*
     * Still accepted so documents written before this was removed keep parsing, but
     * nothing writes it any more: `config/event` is world-readable and this field
     * held an administrator's e-mail address. The same fact lives in `auditLog`,
     * which only an admin can read.
     */
    updatedBy: z.string().optional(),
  })
  .refine((config) => uniqueBy(config.items, (item) => item.sku), {
    message: 'sku ซ้ำกัน',
    path: ['items'],
  })

export type EventConfig = z.infer<typeof EventConfigSchema>

/**
 * What `event.config.ts` is allowed to look like: same shape, but `closeAt` may be
 * an ISO string and everything with a default may be omitted.
 */
export type EventConfigInput = Omit<
  EventConfig,
  'closeAt' | 'pendingExpiryMinutes' | 'paymentMode' | 'items' | 'updatedAt' | 'updatedBy'
> & {
  closeAt: string | Date
  pendingExpiryMinutes?: number
  paymentMode?: EventConfig['paymentMode']
  items: (Omit<EventItem, 'colors'> & { colors?: ColorOption[] })[]
}

/**
 * The subset the public order pages need.
 *
 * `contactEmail` stays: it is printed on the page on purpose. `sender` does not —
 * noreply@ is plumbing, and showing it invites replies nobody reads.
 */
export const PublicEventConfigSchema = EventConfigSchema.innerType().omit({
  sender: true,
})
export type PublicEventConfig = z.infer<typeof PublicEventConfigSchema>

export const toPublicEventConfig = (config: EventConfig): PublicEventConfig => {
  const { sender: _s, ...rest } = config
  return rest
}

/**
 * What `adminUpdateEvent` accepts. The organiser may change anything about what is
 * sold and when — but not `orderPrefix`, because every existing order id starts
 * with it and changing it would orphan them.
 */
export const EventConfigUpdateSchema = EventConfigSchema.innerType().omit({
  orderPrefix: true,
  updatedAt: true,
  updatedBy: true,
})
export type EventConfigUpdate = z.infer<typeof EventConfigUpdateSchema>
